import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, qs } from "../api";
import { Alert, Badge, Button, Card, DataTable, Empty, ErrorBox, FormError, Loading, Modal, Money, MoneyInput, PageHeader, SelectInput, TextInput, useToast } from "../ui";
import { date, dateTime } from "../format";
import { formatGBP, parsePounds } from "../../shared/money";
import { londonDate } from "../../shared/dates";
import { moneyAccountOptions, useAccounts } from "./Purchases";

const TYPE_LABEL: Record<string, string> = { asset: "What the business has", liability: "What the business owes", equity: "Owner's stake", income: "Income", expense: "Costs" };

export default function FinanceHub() {
  const accounts = useAccounts();
  const [modal, setModal] = useState<null | "transfer" | "owner" | "asset" | "opening" | "adjust" | "account">(null);
  if (accounts.isLoading) return <Loading />;
  if (accounts.isError) return <ErrorBox error={accounts.error} />;
  const rows = accounts.data.accounts.filter((a: any) => a.balance_pence !== 0 || a.is_money);
  return (
    <>
      <PageHeader title="Money & accounts" sub={`Balances at ${date(accounts.data.asOf)}. Use these forms for anything that isn't a customer sale, supplier bill, expense or wage.`}
        actions={<Link className="btn btn-secondary" to="/finance/ledger">View ledger</Link>} />
      <div className="tiles">
        {[
          ["transfer", "Move money / card payout", "Bank cash, card settlements with fees"],
          ["owner", "Owner & loan money", "Capital in, drawings, director loan, loans"],
          ["asset", "Buy equipment", "Tools and machines kept long-term"],
          ["opening", "Opening balances", "Starting position when going live"],
          ["adjust", "Accountant adjustment", "Balanced journal for corrections"],
          ["account", "Add category / account", "New expense category or bank account"],
        ].map(([k, l, b]) => <button key={k} type="button" className="tile" style={{ textAlign: "left", cursor: "pointer" }} onClick={() => setModal(k as any)}><div className="label">{l}</div><div className="basis">{b}</div></button>)}
      </div>
      {(["asset", "liability", "equity"] as const).map((t) => {
        const rs = rows.filter((a: any) => a.type === t);
        if (!rs.length) return null;
        return (
          <Card key={t} title={TYPE_LABEL[t]} pad={false}>
            <DataTable rows={rs} rowLink={(a: any) => `/finance/ledger?account=${a.id}`} columns={[
              { key: "c", label: "Code", render: (a: any) => a.code },
              { key: "n", label: "Account", render: (a: any) => <>{a.name} {a.is_money ? <Badge tone="blue">money</Badge> : null}</> },
              { key: "b", label: "Balance", num: true, render: (a: any) => <Money pence={t === "asset" ? a.balance_pence : -a.balance_pence} /> },
            ]} />
          </Card>
        );
      })}
      {modal === "transfer" && <TransferModal onClose={() => setModal(null)} />}
      {modal === "owner" && <OwnerMoneyModal onClose={() => setModal(null)} />}
      {modal === "asset" && <AssetModal onClose={() => setModal(null)} />}
      {modal === "opening" && <JournalModal kind="opening" onClose={() => setModal(null)} />}
      {modal === "adjust" && <JournalModal kind="adjust" onClose={() => setModal(null)} />}
      {modal === "account" && <AccountModal onClose={() => setModal(null)} />}
    </>
  );
}

function useDone(msg: string, onClose: () => void) {
  const qc = useQueryClient();
  const toast = useToast();
  return () => { ["accounts", "journals", "report", "bank"].forEach((k) => qc.invalidateQueries({ queryKey: [k] })); toast("success", msg); onClose(); };
}

function TransferModal({ onClose }: { onClose: () => void }) {
  const accounts = useAccounts();
  const [f, setF] = useState({ fromAccountId: "acc-1020", toAccountId: "acc-1010", amount: "", fee: "", date: londonDate(), memo: "" });
  const m = useMutation({ mutationFn: () => api.post("/finance/transfers", { ...f, fee: f.fee || "0" }), onSuccess: useDone("Transfer recorded", onClose) });
  const opts = moneyAccountOptions(accounts.data?.accounts);
  return (
    <Modal title="Move money between accounts" onClose={onClose} footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" loading={m.isPending} onClick={() => m.mutate()}>Save</Button></>}>
      <div className="form-grid">
        <SelectInput label="From" value={f.fromAccountId} onChange={(e) => setF({ ...f, fromAccountId: e.target.value })} options={opts} />
        <SelectInput label="To" value={f.toAccountId} onChange={(e) => setF({ ...f, toAccountId: e.target.value })} options={opts} />
        <MoneyInput label="Amount arriving" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} />
        <MoneyInput label="Fee deducted (card payouts)" value={f.fee} onChange={(e) => setF({ ...f, fee: e.target.value })} hint="Leave blank for a plain transfer" />
        <TextInput label="Date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} />
        <TextInput label="Note" value={f.memo} onChange={(e) => setF({ ...f, memo: e.target.value })} />
      </div>
      <FormError error={m.error} />
    </Modal>
  );
}

function OwnerMoneyModal({ onClose }: { onClose: () => void }) {
  const accounts = useAccounts();
  const [f, setF] = useState({ type: "capital_in", amount: "", moneyAccountId: "acc-1010", date: londonDate(), memo: "" });
  const m = useMutation({ mutationFn: () => api.post("/finance/owner-money", f), onSuccess: useDone("Recorded", onClose) });
  return (
    <Modal title="Owner, director & loan money" onClose={onClose} footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" loading={m.isPending} onClick={() => m.mutate()}>Save</Button></>}>
      <Alert kind="info">Kept separate from sales and expenses — none of these affect profit.</Alert>
      <div className="form-grid" style={{ marginTop: 12 }}>
        <SelectInput label="Type" full value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} options={[
          { value: "capital_in", label: "Owner put money into the business" }, { value: "drawings", label: "Owner took money out (drawings)" },
          { value: "director_loan_in", label: "Director lent money to the business" }, { value: "director_loan_repaid", label: "Business repaid the director" },
          { value: "loan_in", label: "Loan received (bank/finance)" }, { value: "loan_repaid", label: "Loan capital repaid" },
        ]} />
        <MoneyInput label="Amount" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} />
        <SelectInput label="Money account" value={f.moneyAccountId} onChange={(e) => setF({ ...f, moneyAccountId: e.target.value })} options={moneyAccountOptions(accounts.data?.accounts)} />
        <TextInput label="Date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} />
        <TextInput label="Note" value={f.memo} onChange={(e) => setF({ ...f, memo: e.target.value })} />
      </div>
      <FormError error={m.error} />
    </Modal>
  );
}

function AssetModal({ onClose }: { onClose: () => void }) {
  const accounts = useAccounts();
  const [f, setF] = useState({ name: "", purchaseDate: londonDate(), cost: "", paidFromAccountId: "acc-1010", notes: "" });
  const m = useMutation({ mutationFn: () => api.post("/finance/assets", f), onSuccess: useDone("Equipment recorded", onClose) });
  const opts = [...moneyAccountOptions(accounts.data?.accounts), { value: "acc-2300", label: "Paid personally by director (director loan)" }];
  return (
    <Modal title="Buy equipment" onClose={onClose} footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" loading={m.isPending} onClick={() => m.mutate()}>Save</Button></>}>
      <p className="muted">For tools and machines the business keeps (e.g. a ramp or diagnostic machine). Recorded as an asset, not a cost — your accountant handles depreciation and capital allowances.</p>
      <div className="form-grid">
        <TextInput label="What was bought" full value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <MoneyInput label="Cost" value={f.cost} onChange={(e) => setF({ ...f, cost: e.target.value })} />
        <TextInput label="Date" type="date" value={f.purchaseDate} onChange={(e) => setF({ ...f, purchaseDate: e.target.value })} />
        <SelectInput label="Paid from" full value={f.paidFromAccountId} onChange={(e) => setF({ ...f, paidFromAccountId: e.target.value })} options={opts} />
      </div>
      <FormError error={m.error} />
    </Modal>
  );
}

function AccountModal({ onClose }: { onClose: () => void }) {
  const [f, setF] = useState({ name: "", kind: "expense" });
  const m = useMutation({ mutationFn: () => api.post("/finance/accounts", f), onSuccess: useDone("Account added", onClose) });
  return (
    <Modal title="Add category or account" onClose={onClose} footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" loading={m.isPending} disabled={!f.name.trim()} onClick={() => m.mutate()}>Add</Button></>}>
      <div className="form-grid">
        <SelectInput label="Kind" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} options={[{ value: "expense", label: "Expense category" }, { value: "bank", label: "Another bank account" }, { value: "cash", label: "Another cash float" }]} />
        <TextInput label="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
      </div>
      <FormError error={m.error} />
    </Modal>
  );
}

function JournalModal({ kind, onClose }: { kind: "opening" | "adjust"; onClose: () => void }) {
  const accounts = useAccounts();
  const [d, setD] = useState(londonDate());
  const [desc, setDesc] = useState("");
  const [lines, setLines] = useState([{ accountId: "", debit: "", credit: "", memo: "" }, { accountId: "", debit: "", credit: "", memo: "" }]);
  const m = useMutation({
    mutationFn: () => api.post(kind === "opening" ? "/finance/opening-balances" : "/finance/adjustments", { date: d, description: desc, lines: lines.filter((l) => l.accountId).map((l) => ({ ...l, debit: l.debit || "0", credit: l.credit || "0" })) }),
    onSuccess: useDone(kind === "opening" ? "Opening balances posted" : "Adjustment posted", onClose),
  });
  const dr = lines.reduce((a, l) => a + (parsePounds(l.debit) ?? 0), 0);
  const cr = lines.reduce((a, l) => a + (parsePounds(l.credit) ?? 0), 0);
  const set = (i: number, p: any) => setLines(lines.map((l, k) => (k === i ? { ...l, ...p } : l)));
  return (
    <Modal wide title={kind === "opening" ? "Opening balances" : "Accountant adjustment"} onClose={onClose}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" loading={m.isPending} disabled={kind === "adjust" && (dr !== cr || !desc.trim())} onClick={() => m.mutate()}>Post</Button></>}>
      <Alert kind="info">{kind === "opening" ? "Enter balances on the go-live date (e.g. bank and cash from statements, money owed). Any difference is balanced to Opening balance equity." : "For corrections your accountant gives you. Debits must equal credits. Everything is recorded in the audit trail."}</Alert>
      <div className="form-grid" style={{ marginTop: 12 }}>
        <TextInput label="Date" type="date" value={d} onChange={(e) => setD(e.target.value)} />
        {kind === "adjust" && <TextInput label="Description" value={desc} onChange={(e) => setDesc(e.target.value)} />}
      </div>
      <div className="table-wrap"><table className="table" style={{ marginTop: 12 }}><thead><tr><th>Account</th><th className="num">Debit £</th><th className="num">Credit £</th><th>Note</th></tr></thead><tbody>
        {lines.map((l, i) => <tr key={i}>
          <td><select className="input" aria-label="Account" value={l.accountId} onChange={(e) => set(i, { accountId: e.target.value })}><option value="">—</option>{(accounts.data?.accounts ?? []).map((a: any) => <option key={a.id} value={a.id}>{a.code} {a.name}</option>)}</select></td>
          <td><input className="input right" aria-label="Debit" value={l.debit} onChange={(e) => set(i, { debit: e.target.value })} /></td>
          <td><input className="input right" aria-label="Credit" value={l.credit} onChange={(e) => set(i, { credit: e.target.value })} /></td>
          <td><input className="input" aria-label="Note" value={l.memo} onChange={(e) => set(i, { memo: e.target.value })} /></td>
        </tr>)}
      </tbody><tfoot><tr><td>Totals</td><td className="num">{formatGBP(dr)}</td><td className="num">{formatGBP(cr)}</td><td>{dr !== cr && (kind === "adjust" ? <Badge tone="red">Not balanced</Badge> : <span className="small muted">{formatGBP(Math.abs(dr - cr))} to opening equity</span>)}</td></tr></tfoot></table></div>
      <Button size="sm" onClick={() => setLines([...lines, { accountId: "", debit: "", credit: "", memo: "" }])}>+ Line</Button>
      <FormError error={m.error} />
    </Modal>
  );
}

export function LedgerPage() {
  const [params, setParams] = useSearchParams();
  const account = params.get("account") ?? "";
  const from = params.get("from") ?? "";
  const to = params.get("to") ?? "";
  const accounts = useAccounts();
  const q = useQuery({ queryKey: ["journals", account, from, to], queryFn: () => api.get(`/finance/journals${qs({ account, from, to })}`) });
  const [reversing, setReversing] = useState<any>(null);
  const set = (k: string, v: string) => { const p = new URLSearchParams(params); if (v) p.set(k, v); else p.delete(k); setParams(p, { replace: true }); };
  return (
    <>
      <PageHeader title="Ledger" back={{ to: "/finance", label: "Money & accounts" }} sub="Every accounting entry, linked to the record that created it. Entries are never edited — corrections are separate reversing entries." />
      <div className="filters">
        <SelectInput label="Account" value={account} onChange={(e) => set("account", e.target.value)} placeholder="All accounts" options={(accounts.data?.accounts ?? []).map((a: any) => ({ value: a.id, label: `${a.code} ${a.name}` }))} />
        <TextInput label="From" type="date" value={from} onChange={(e) => set("from", e.target.value)} />
        <TextInput label="To" type="date" value={to} onChange={(e) => set("to", e.target.value)} />
      </div>
      {q.isLoading ? <Loading /> : q.isError ? <ErrorBox error={q.error} /> : !q.data.journals.length ? <Card><Empty title="No entries" /></Card> : (
        <div className="stack">
          {q.data.journals.map((j: any) => (
            <Card key={j.id} title={<>{date(j.date)} · {j.description} <Badge>{j.source_type}</Badge> {j.reversed_by && <Badge tone="red">reversed</Badge>}</>}
              actions={<><span className="small muted">{j.created_by_name ?? "system"} · {dateTime(j.created_at)}</span>{!j.reversed_by && ["adjustment", "opening", "transfer", "owner"].includes(j.source_type) && <Button size="sm" variant="ghost" onClick={() => setReversing(j)}>Reverse</Button>}</>} pad={false}>
              <DataTable rows={j.lines} columns={[
                { key: "a", label: "Account", render: (l: any) => `${l.code} ${l.account_name}` },
                { key: "m", label: "Note", render: (l: any) => l.memo?.startsWith("staff:") ? "" : l.memo ?? "" },
                { key: "d", label: "Debit", num: true, render: (l: any) => (l.debit_pence ? formatGBP(l.debit_pence) : "") },
                { key: "c", label: "Credit", num: true, render: (l: any) => (l.credit_pence ? formatGBP(l.credit_pence) : "") },
              ]} />
            </Card>
          ))}
        </div>
      )}
      {reversing && <ReverseModal j={reversing} onClose={() => setReversing(null)} />}
    </>
  );
}

function ReverseModal({ j, onClose }: { j: any; onClose: () => void }) {
  const [reason, setReason] = useState("");
  const m = useMutation({ mutationFn: () => api.post(`/finance/journals/${j.id}/reverse`, { reason }), onSuccess: useDone("Entry reversed", onClose) });
  return (
    <Modal title="Reverse entry" onClose={onClose} footer={<><Button onClick={onClose}>Cancel</Button><Button variant="danger" disabled={reason.trim().length < 3} loading={m.isPending} onClick={() => m.mutate()}>Reverse</Button></>}>
      <p>{j.description}</p>
      <TextInput label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} />
      <FormError error={m.error} />
    </Modal>
  );
}
